// Polished top bar:
//   - Mobile hamburger to open the sidebar drawer
//   - Search pill that opens the ⌘K command palette
//   - Help (?) shortcut button
//   - Notifications bell w/ unread badge
//   - Avatar dropdown showing real Entra identity, theme switcher,
//     workspace settings, sign out

import { useState } from "react";
import { Search, HelpCircle, Menu } from "lucide-react";
import NotificationsBell from "./notifications-bell";
import ProfileMenu from "./profile-menu";
import CommandPalette from "./command-palette";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";

interface Props {
  onOpenDrawer?: () => void;
  onOpenHelp?: () => void;
}

export default function Topbar({ onOpenDrawer, onOpenHelp }: Props) {
  const [paletteOpen, setPaletteOpen] = useState(false);

  useKeyboardShortcuts(
    [
      { key: "k", meta: true, force: true, handler: () => setPaletteOpen((o) => !o) },
      { key: "/", handler: () => setPaletteOpen(true) },
    ],
    true,
  );

  return (
    <>
      <header className="flex h-[52px] shrink-0 items-center gap-2 border-b border-line bg-surface-panel/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-surface-panel/80 sm:gap-3 sm:px-4">
        {onOpenDrawer && (
          <button
            onClick={onOpenDrawer}
            className="rounded p-1.5 text-ink-muted hover:bg-surface-hover hover:text-ink md:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}

        {/* Spacer pushes everything else to the top-right (standard pattern:
            workspace nav lives in the left sidebar, top bar = utility cluster). */}
        <div className="flex-1" />

        <button
          onClick={() => setPaletteOpen(true)}
          className="group relative flex h-9 w-full max-w-xs items-center gap-2 rounded-full border border-line bg-surface-panel2 pl-3 pr-2 text-sm text-ink-dim transition-colors hover:border-brand/30 hover:bg-surface-hover hover:text-ink focus:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/20 sm:max-w-sm"
        >
          <Search className="h-3.5 w-3.5 shrink-0" />
          <span className="hidden flex-1 truncate text-left sm:inline">Search…</span>
          <span className="flex-1 truncate text-left sm:hidden">Search</span>
          <span className="hidden shrink-0 items-center gap-0.5 rounded border border-line bg-surface-panel px-1.5 py-0.5 font-mono text-[10px] text-ink-dim md:inline-flex">
            <span>⌘</span><span>K</span>
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-1">
          {onOpenHelp && (
            <button
              onClick={onOpenHelp}
              className="hidden rounded p-1.5 text-ink-muted hover:bg-surface-hover hover:text-ink md:block"
              aria-label="Keyboard shortcuts"
              title="Shortcuts (?)"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          )}
          <NotificationsBell />
          <div className="mx-1 hidden h-5 w-px bg-line sm:block" />
          <ProfileMenu />
        </div>
      </header>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  );
}
